import { gql, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Columns,
  Container,
  Divider,
  Heading,
  Stack,
  Text,
  Visible,
} from '@spaceship-fspl/components';
import {
  SaverPortfolio,
  saverPortfolioInformationScalars,
} from '@spaceship-fspl/graphql';
import {
  WebVoyagerPortfolioPage,
  WebVoyagerPortfolioPageVariables,
} from '@spaceship-fspl/graphql/src/__generated__/WebVoyagerPortfolioPage';
import { ExternalRoutes } from '@spaceship-fspl/helpers';
import {
  VoyagerEarthStarsIcon,
  VoyagerExplorerStarsIcon,
  VoyagerGalaxyStarsIcon,
  VoyagerOriginStarsIcon,
  VoyagerUniverseStarsIcon,
} from '@spaceship-fspl/icons-web';
import {
  height,
  linearGradient,
  LinearGradientConfig,
  match,
  width,
} from '@spaceship-fspl/styles';
import {
  getHeroPageLayoutStaticProps,
  getMetadataPropsFromPrismicSlice,
  HeaderProps,
  ImportantDocumentsPanel,
  mapNodeToHeaderProps,
  Metadata,
  MetadataProps,
  PanelSection,
  ProxyVotingPanel,
  ReturnsPanel,
  RiskPanel,
  StickyHeader,
  VOYAGER_BASIC_DOCUMENTS,
  VoyagerFeesForPortfolioPanel,
  withHeroPageLayout,
} from 'components';
import {
  getHeader,
  getVoyagerPortfolio,
  getWorldMap,
  mapSlicesToProps,
  RichText,
} from 'data/prismic';
import {
  Voyager_PortfolioBodyCompany_Prices,
  Voyager_PortfolioBodyCompany_PricesPrimary,
  Voyager_PortfolioBodyCompanylocations,
  Voyager_PortfolioBodyCompanylocationsFields,
  Voyager_PortfolioBodyCompanylocationsPrimary,
  Voyager_PortfolioBodyDescription,
  Voyager_PortfolioBodyDescriptionPrimary,
  Voyager_PortfolioBodyExcluded_Companies,
  Voyager_PortfolioBodyExcluded_CompaniesFields,
  Voyager_PortfolioBodyExcluded_CompaniesPrimary,
  Voyager_PortfolioBodyFees,
  Voyager_PortfolioBodyFeesFields,
  Voyager_PortfolioBodyFeesPrimary,
  Voyager_PortfolioBodyHave_Your_Say,
  Voyager_PortfolioBodyHave_Your_SayPrimary,
  Voyager_PortfolioBodyHero,
  Voyager_PortfolioBodyHeroPrimary,
  Voyager_PortfolioBodyImportant_Documents,
  Voyager_PortfolioBodyImportant_DocumentsPrimary,
  Voyager_PortfolioBodyReturns,
  Voyager_PortfolioBodyReturnsPrimary,
  Voyager_PortfolioBodyRisks,
  Voyager_PortfolioBodyRisksFields,
  Voyager_PortfolioBodyRisksPrimary,
  World_MapCountries,
} from 'data/prismic/types';
import { VoyagerPrismicPortfolio } from 'helpers/products';
import { GetStaticPaths, NextPage } from 'next';
import dynamic from 'next/dynamic';
import { ParsedUrlQuery } from 'querystring';
import * as React from 'react';
import styled from 'styled-components';

import { CompaniesInPortfolioPanelProps } from './companies-in-portfolio-panel';
import EarthHeroImageDesktop from './img-hero-earth.svg';
import ExplorerHeroImageDesktop from './img-hero-explorer.svg';
import GalaxyHeroImageDesktop from './img-hero-galaxy.svg';
import OriginHeroImageDesktop from './img-hero-origin.svg';
import UniverseHeroImageDesktop from './img-hero-universe.svg';
import { LocationsPanel } from './locations-panel';
import { PortfolioDescriptionPanel } from './portfolio-description-panel';

const CompaniesInPortfolioPanel = dynamic<CompaniesInPortfolioPanelProps>(
  import('./companies-in-portfolio-panel').then(
    ({ CompaniesInPortfolioPanel }) => CompaniesInPortfolioPanel,
    // @see https://github.com/vercel/next.js/issues/4515#issuecomment-447570395
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) as any,
  { ssr: false },
);

const defaultBackgroundGradient: LinearGradientConfig = {
  angle: '22.62deg',
  colorStops: [
    ['indigo.050', '0.07%'],
    ['red.020', '108.95%'],
  ],
};

const earthBackgroundGradient: LinearGradientConfig = {
  alpha: 0.8,
  angle: '76.52deg',
  colorStops: [
    ['indigo.050', '-39.71%'],
    ['blue.050', '159.62%'],
  ],
};

const voyagerBackgroundGradient: LinearGradientConfig = {
  alpha: 0.95,
  angle: '45deg',
  colorStops: [
    ['indigo.100', '0%'],
    ['indigo.050', '100%'],
  ],
};

const HeroPanel = styled.div<{ backgroundGradient: LinearGradientConfig }>`
  background: ${({ backgroundGradient }) => linearGradient(backgroundGradient)};
  min-height: 500px;
`;

const HeroPanelDesktopImage = styled.div<{ imgSrc: string }>`
  ${height({ md: 437, lg: 636 })}
  ${width({ md: 275, lg: 400 })}
  display: none;
  margin: auto;
  max-width: 100%;

  ${({ imgSrc }) =>
    match('md')`
      background: url(${imgSrc}) no-repeat;
      background-position: center;
      background-size: contain;
      display: block;
    `}
`;

const ExcludeFeatureImage = styled.img`
  ${width({ xs: 48, lg: 70 })}
  display: block;
`;

interface VoyagerPortfolioProps {
  portfolio: VoyagerPrismicPortfolio;
  headerProps?: HeaderProps;
  data: {
    Voyager_portfolioBodyMetadata?: MetadataProps;
    Voyager_portfolioBodyHero?: Voyager_PortfolioBodyHeroPrimary | null;
    Voyager_portfolioBodyReturns?: Voyager_PortfolioBodyReturnsPrimary | null;
    Voyager_portfolioBodyDescription?: Voyager_PortfolioBodyDescriptionPrimary | null;
    Voyager_portfolioBodyExcluded_companies?:
      | (Voyager_PortfolioBodyExcluded_CompaniesPrimary & {
          excludes?: Voyager_PortfolioBodyExcluded_CompaniesFields[] | null;
        })
      | null;
    Voyager_portfolioBodyHave_your_say?: Voyager_PortfolioBodyHave_Your_SayPrimary | null;
    Voyager_portfolioBodyCompanylocations?:
      | null
      | (Voyager_PortfolioBodyCompanylocationsPrimary & {
          locations?: Voyager_PortfolioBodyCompanylocationsFields[] | null;
        });
    Voyager_portfolioBodyCompany_prices?: Voyager_PortfolioBodyCompany_PricesPrimary | null;
    Voyager_portfolioBodyFees?:
      | (Voyager_PortfolioBodyFeesPrimary & {
          fees?: Voyager_PortfolioBodyFeesFields[] | null;
        })
      | null;
    Voyager_portfolioBodyRisks?:
      | null
      | (Voyager_PortfolioBodyRisksPrimary & {
          risks?: Voyager_PortfolioBodyRisksFields[] | null;
        });
    Voyager_portfolioBodyImportant_documents?: Voyager_PortfolioBodyImportant_DocumentsPrimary | null;
  };
  countryCoordinates?: World_MapCountries[] | null;
}

const VoyagerPortfolio: NextPage<VoyagerPortfolioProps> = ({
  portfolio,
  headerProps,
  data: {
    Voyager_portfolioBodyHero: hero,
    Voyager_portfolioBodyMetadata: metadata,
    Voyager_portfolioBodyReturns: returns,
    Voyager_portfolioBodyDescription: description,
    Voyager_portfolioBodyExcluded_companies: excludedCompanies,
    Voyager_portfolioBodyHave_your_say: haveYourSay,
    Voyager_portfolioBodyCompanylocations: companyLocations,
    Voyager_portfolioBodyCompany_prices: companyPrices,
    Voyager_portfolioBodyFees: fees,
    Voyager_portfolioBodyRisks: risks,
    Voyager_portfolioBodyImportant_documents: documents,
  },
  countryCoordinates,
}) => {
  const gqlPortfolio = mapParamToPortfolio(portfolio);
  const { desktopImage, MobileIconSVG } = {
    [SaverPortfolio.INDEX]: {
      desktopImage: OriginHeroImageDesktop,
      MobileIconSVG: VoyagerOriginStarsIcon,
    },
    [SaverPortfolio.UNIVERSE]: {
      desktopImage: UniverseHeroImageDesktop,
      MobileIconSVG: VoyagerUniverseStarsIcon,
    },
    [SaverPortfolio.EARTH]: {
      desktopImage: EarthHeroImageDesktop,
      MobileIconSVG: VoyagerEarthStarsIcon,
    },
    [SaverPortfolio.GALAXY]: {
      desktopImage: GalaxyHeroImageDesktop,
      MobileIconSVG: VoyagerGalaxyStarsIcon,
    },
    [SaverPortfolio.EXPLORER]: {
      desktopImage: ExplorerHeroImageDesktop,
      MobileIconSVG: VoyagerExplorerStarsIcon,
    },
  }[gqlPortfolio];

  const heroBackground = {
    [SaverPortfolio.INDEX]: defaultBackgroundGradient,
    [SaverPortfolio.UNIVERSE]: defaultBackgroundGradient,
    [SaverPortfolio.EARTH]: earthBackgroundGradient,
    [SaverPortfolio.GALAXY]: voyagerBackgroundGradient,
    [SaverPortfolio.EXPLORER]: voyagerBackgroundGradient,
  }[gqlPortfolio];

  const resp = useQuery<
    WebVoyagerPortfolioPage,
    WebVoyagerPortfolioPageVariables
  >(
    gql`
      query WebVoyagerPortfolioPage($portfolio: SaverPortfolio!) {
        saverPortfolioInformation(portfolio: $portfolio) {
          id
          ...voyagerPortfolioInformationFields
        }
      }
      ${saverPortfolioInformationScalars}
    `,
    {
      variables: {
        portfolio: gqlPortfolio,
      },
    },
  );
  const portfolioData = resp.data?.saverPortfolioInformation;

  return (
    <>
      {metadata && <Metadata {...metadata} />}
      <HeroPanel backgroundGradient={heroBackground}>
        <StickyHeader
          {...headerProps}
          buttonVariant={{ xs: 'primary', lg: 'secondary' }}
        />

        <Container>
          <Box paddingY={{ xs: 'lg', md: 'xl' }}>
            <Columns alignX="center" alignY="center" spaceX="md" spaceY="md">
              <Columns.Column width={{ xs: 1, md: 1 / 2 }}>
                <HeroPanelDesktopImage imgSrc={desktopImage} />

                <Visible isHidden={{ md: true }}>
                  <Stack alignX="center">
                    <MobileIconSVG size="xxxl" color="neutral.000" />
                  </Stack>
                </Visible>
              </Columns.Column>

              <Columns.Column width={{ xs: 1, md: 1 / 2 }}>
                <Stack spaceY="md" alignX={{ xs: 'center', md: 'left' }}>
                  <Text
                    variant={2}
                    color="neutral.000"
                    align={{ xs: 'center', md: 'left' }}
                  >
                    Spaceship Voyager
                  </Text>

                  <Heading
                    variant={1}
                    color="neutral.000"
                    align={{ xs: 'center', md: 'left' }}
                    component="h1"
                  >
                    {hero?.title ?? ''}
                  </Heading>

                  <Text
                    variant={1}
                    color="neutral.000"
                    align={{ xs: 'center', md: 'left' }}
                  >
                    {hero?.tagline ?? ''}
                  </Text>

                  <Button
                    variant="secondary"
                    size="lg"
                    href={ExternalRoutes.VOYAGER_SIGNUP}
                  >
                    Sign up
                  </Button>
                </Stack>
              </Columns.Column>
            </Columns>
          </Box>
        </Container>
      </HeroPanel>

      {returns && (
        <ReturnsPanel
          title={returns.title ?? ''}
          tagline={returns.tagline ?? ''}
          disclaimer={<RichText render={returns.richdisclaimer} />}
          portfolio={{
            type: portfolio,
            title: portfolioData?.title ?? '',
            inceptionDate: portfolioData?.inceptionDate,
            sinceInceptionPerformancePercentage:
              portfolioData?.sinceInceptionPerformancePercentage,
            oneMonthPerformancePercentage:
              portfolioData?.oneMonthPerformancePercentage,
            threeMonthPerformancePercentage:
              portfolioData?.threeMonthPerformancePercentage,
            sixMonthPerformancePercentage:
              portfolioData?.sixMonthPerformancePercentage,
            oneYearPerformancePercentage:
              portfolioData?.oneYearPerformancePercentage,
            threeYearPerformancePercentage:
              portfolioData?.threeYearPerformancePercentage,
            performanceAsAt: portfolioData?.performanceAsAt,
          }}
        />
      )}

      {description && (
        <PortfolioDescriptionPanel
          title={<RichText render={description.richtitle} />}
          tagline={<RichText render={description.richtagline} />}
        />
      )}

      {excludedCompanies && (
        <PanelSection>
          <Container>
            <Stack spaceY={{ xs: 'lg', lg: 'xl' }}>
              <Columns alignX="center">
                <Columns.Column width={{ md: 3 / 4, lg: 5 / 6 }}>
                  <Stack spaceY="sm">
                    <Heading
                      component="h2"
                      variant={2}
                      color="neutral.100"
                      align={{ md: 'center' }}
                    >
                      {excludedCompanies.title}
                    </Heading>

                    <Text
                      variant={1}
                      color="neutral.085"
                      component="div"
                      align={{ md: 'center' }}
                    >
                      <RichText render={excludedCompanies.richtagline} />
                    </Text>
                  </Stack>
                </Columns.Column>
              </Columns>

              <Columns
                spaceX={{ md: 'md' }}
                spaceY={{ xs: 'xxs', md: 'md' }}
                alignX="center"
              >
                {excludedCompanies.excludes?.map(
                  ({ icon, exclude_description: description }, index) => (
                    <Columns.Column
                      key={description || index}
                      width={{ xs: 1, md: 1 / 4 }}
                    >
                      <Box
                        alignItems="center"
                        backgroundColor="neutral.015"
                        borderRadius="sm"
                        display="flex"
                        flexDirection={{ xs: 'row', md: 'column' }}
                        paddingX={{ xs: 'md', md: 'sm', lg: 'lg' }}
                        paddingY={{ xs: 'sm', md: 'xl' }}
                        height="100%"
                      >
                        <Box
                          marginRight={{ xs: 'md', md: 'none' }}
                          marginBottom={{ md: 'sm' }}
                        >
                          <ExcludeFeatureImage src={icon.url} />
                        </Box>

                        <Box display="flex" flex={1} alignItems="center">
                          <Text
                            variant={1}
                            color="neutral.100"
                            align={{ md: 'center' }}
                          >
                            {description}
                          </Text>
                        </Box>
                      </Box>
                    </Columns.Column>
                  ),
                )}
              </Columns>

              {!!excludedCompanies.richdisclaimer && (
                <Columns alignX="center">
                  <Columns.Column width={{ md: 5 / 6 }}>
                    <Text
                      variant={3}
                      align="center"
                      color="neutral.085"
                      component="div"
                    >
                      <RichText render={excludedCompanies.richdisclaimer} />
                    </Text>
                  </Columns.Column>
                </Columns>
              )}
            </Stack>
          </Container>
        </PanelSection>
      )}

      {haveYourSay && (
        <ProxyVotingPanel
          title={haveYourSay.title ?? ''}
          tagline={<RichText render={haveYourSay.richtagline} />}
        />
      )}

      {companyLocations && companyLocations.title && (
        <LocationsPanel
          title={companyLocations.title || ''}
          disclaimer={companyLocations.disclaimer || ''}
          data={(companyLocations.locations || []).map(
            ({ country_key, percentage }) => {
              const country = countryCoordinates?.find(
                ({ key }) => key === country_key,
              );

              return {
                country: country?.display_name || '',
                percentage: percentage || 0,
                x: country?.x || 0,
                y: country?.y || 0,
              };
            },
          )}
        />
      )}

      <CompaniesInPortfolioPanel
        portfolio={gqlPortfolio}
        title={companyPrices?.title || ''}
        disclaimer={companyPrices?.disclaimer || ''}
      />

      {fees && (
        <VoyagerFeesForPortfolioPanel
          backgroundColor="neutral.015"
          title={fees.title || ''}
          tagline={<RichText render={fees.richtagline} />}
          disclaimer={<RichText render={fees.richdisclaimer} />}
          portfolio={portfolioData ?? undefined}
        />
      )}

      {risks && (
        <RiskPanel
          variant="voyager"
          title={risks.title || ''}
          tagline={risks.tagline || ''}
          risks={risks.risks?.map((risk) => ({
            iconURL: risk.icon_img.url,
            name: risk.title || '',
            description: risk.description || '',
          }))}
          disclaimer={<RichText render={risks.richdisclaimer} />}
        />
      )}

      {documents && (
        <>
          <Divider color="neutral.050" />
          <ImportantDocumentsPanel
            title={documents.title || ''}
            tagline={documents.summary || ''}
            documents={VOYAGER_BASIC_DOCUMENTS}
          />
        </>
      )}
    </>
  );
};

type PortfolioParameter =
  | 'origin'
  | 'universe'
  | 'earth'
  | 'galaxy'
  | 'explorer';

export const getStaticPaths: GetStaticPaths<{
  portfolio: PortfolioParameter;
}> = async () => {
  const paths: Array<{ params: { portfolio: PortfolioParameter } }> = [
    { params: { portfolio: 'universe' } },
    { params: { portfolio: 'origin' } },
    { params: { portfolio: 'earth' } },
    { params: { portfolio: 'galaxy' } },
    { params: { portfolio: 'explorer' } },
  ];

  return {
    paths,
    fallback: false,
  };
};

interface VoyagerPortfolioContext extends ParsedUrlQuery {
  portfolio: PortfolioParameter;
}

const mapParamToPortfolio = (param?: PortfolioParameter): SaverPortfolio => {
  switch (param) {
    case 'origin':
      return SaverPortfolio.INDEX;
    case 'universe':
      return SaverPortfolio.UNIVERSE;
    case 'earth':
      return SaverPortfolio.EARTH;
    case 'galaxy':
      return SaverPortfolio.GALAXY;
    case 'explorer':
      return SaverPortfolio.EXPLORER;
    default:
      throw new Error('Invalid or unhandled portfolio parameter');
  }
};

export const getStaticProps = getHeroPageLayoutStaticProps<
  VoyagerPortfolioProps,
  VoyagerPortfolioContext
>(async (context) => {
  const portfolio = context?.params?.portfolio as VoyagerPrismicPortfolio;
  const portfolioId = {
    origin: 'XtBa2hEAACsAUfTy',
    universe: 'Xs3y8BEAACYAIN9z',
    earth: 'X2PmnRIAACMAlrCu',
    galaxy: 'ZjN2XREAAOIscdyM',
    explorer: 'ZjOKOBEAAJ0ucfr2',
  }[portfolio];

  const header = await getHeader();
  const worldMap = await getWorldMap();
  const document = await getVoyagerPortfolio(portfolioId);
  const data = mapSlicesToProps(document, {
    Voyager_portfolioBodyMetadata: (slice) =>
      getMetadataPropsFromPrismicSlice(slice),
    Voyager_portfolioBodyHero: (slice: Voyager_PortfolioBodyHero) => {
      const { title, product, tagline } = slice.primary || {};
      return {
        title,
        product,
        tagline,
      };
    },
    Voyager_portfolioBodyReturns: (slice: Voyager_PortfolioBodyReturns) => {
      const { title, tagline, richdisclaimer } = slice.primary || {};
      return {
        title,
        tagline,
        richdisclaimer,
      };
    },
    Voyager_portfolioBodyCompanylocations: (
      slice: Voyager_PortfolioBodyCompanylocations,
    ) => {
      const { title, disclaimer } = slice.primary || {};
      return {
        title,
        disclaimer,
        locations: slice.fields,
      };
    },
    Voyager_portfolioBodyCompany_prices: (
      slice: Voyager_PortfolioBodyCompany_Prices,
    ) => {
      const { title, disclaimer } = slice.primary || {};
      return {
        title,
        disclaimer,
      };
    },
    Voyager_portfolioBodyDescription: (
      slice: Voyager_PortfolioBodyDescription,
    ) => {
      const { richtitle, richtagline } = slice.primary || {};
      return {
        richtitle,
        richtagline,
      };
    },
    Voyager_portfolioBodyExcluded_companies: (
      slice: Voyager_PortfolioBodyExcluded_Companies,
    ) => {
      const { title, richtagline, richdisclaimer } = slice.primary || {};
      return {
        title,
        richtagline,
        richdisclaimer,
        excludes: slice.fields,
      };
    },
    Voyager_portfolioBodyHave_your_say: (
      slice: Voyager_PortfolioBodyHave_Your_Say,
    ) => {
      const { title, richtagline } = slice.primary || {};
      return {
        title,
        richtagline,
      };
    },
    Voyager_portfolioBodyFees: (slice: Voyager_PortfolioBodyFees) => {
      const { title, richtagline, fee_table_title, richdisclaimer } =
        slice.primary || {};
      return {
        title,
        richtagline,
        fee_table_title,
        richdisclaimer,
        fees: slice.fields,
      };
    },
    Voyager_portfolioBodyRisks: (slice: Voyager_PortfolioBodyRisks) => {
      const { title, tagline, richdisclaimer } = slice.primary || {};
      return { title, tagline, richdisclaimer, risks: slice.fields };
    },
    Voyager_portfolioBodyImportant_documents: (
      slice: Voyager_PortfolioBodyImportant_Documents,
    ) => {
      const { title, summary } = slice.primary || {};
      return {
        title,
        summary,
      };
    },
  });

  return {
    props: {
      portfolio,
      headerProps: mapNodeToHeaderProps(header),
      data,
      countryCoordinates: worldMap.countries,
    },
  };
});

export default withHeroPageLayout({ product: 'voyager' })(VoyagerPortfolio);
